import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";

export function useSavedPosit(positId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    let isCancelled = false;
    async function fetchSaved() {
      if (!user?.id || !positId) {
        setIsSaved(false);
        return;
      }
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("saved_posits")
          .select("id")
          .eq("user_id", user.id)
          .eq("posit_id", positId)
          .maybeSingle();
        if (!isCancelled) {
          if (error && error.code !== "PGRST116") {
            // Ignore not found; otherwise log
          }
          setIsSaved(!!data);
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    }
    fetchSaved();
    return () => {
      isCancelled = true;
    };
  }, [user?.id, positId]);

  const save = useCallback(async () => {
    if (!user) {
      toast({ title: "Sign in required", description: "Please sign in to save Posits.", variant: "destructive" });
      return false;
    }
    if (!positId) return false;
    try {
      setIsLoading(true);
      // Guard against duplicates
      const { data: existing } = await supabase
        .from("saved_posits")
        .select("id")
        .eq("user_id", user.id)
        .eq("posit_id", positId)
        .maybeSingle();
      if (!existing) {
        const { error } = await supabase.from("saved_posits").insert({ user_id: user.id, posit_id: positId });
        if (error) throw error;
      }
      setIsSaved(true);
      return true;
    } catch (e) {
      toast({ title: "Couldn’t save Posit", description: "Please try again.", variant: "destructive" });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, positId, toast]);

  const unsave = useCallback(async () => {
    if (!user || !positId) return false;
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from("saved_posits")
        .delete()
        .eq("user_id", user.id)
        .eq("posit_id", positId);
      if (error) throw error;
      setIsSaved(false);
      return true;
    } catch (e) {
      toast({ title: "Couldn’t unsave Posit", description: "Please try again.", variant: "destructive" });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, positId, toast]);

  const toggle = useCallback(async () => {
    if (isSaved) {
      return await unsave();
    }
    return await save();
  }, [isSaved, save, unsave]);

  return { isSaved, isLoading, save, unsave, toggle };
}


